/**
 * Terms of Service, Acceptable Use Policy and Privacy Policy. The public /legal pages serve signed-out
 * readers (sign-in page, email footers); /policies shows the same documents inside the app layout.
 */

import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getLegalDocuments } from '@/api/legal';
import LegalMarkdown from '@/components/legal/LegalMarkdown';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { apiError } from '@/utils/apiError';

export function DraftNotice({ version }: { version: string }) {
  return (
    <p role="note" className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
      <strong>Draft — not yet in effect.</strong> Version {version}. This text is pending attorney review and may change before it is published.
    </p>
  );
}

function useDocument() {
  const { document: id } = useParams();
  const legal = useQuery({ queryKey: ['legal-documents'], queryFn: getLegalDocuments });
  const document = legal.data?.documents.find((doc) => doc.id === id) ?? legal.data?.documents[0];
  return { legal, document };
}

export default function LegalPage() {
  const { legal, document } = useDocument();
  if (legal.isLoading) return <LoadingSpinner label="Loading..." />;
  if (legal.error || !legal.data || !document) return <p role="alert" className="p-6 text-red-700">{apiError(legal.error)}</p>;
  const others = legal.data.documents.filter((doc) => doc.id !== document.id);
  return (
    <main className="mx-auto max-w-3xl space-y-4 px-4 py-8">
      <p className="text-sm font-semibold text-indigo-700">Vantage by Velosynt</p>
      <h1 className="text-2xl font-bold text-gray-900">{document.title}</h1>
      {legal.data.draft ? <DraftNotice version={legal.data.version} /> : <p className="text-sm text-gray-500">Version {legal.data.version}</p>}
      <nav className="flex gap-4 text-sm">
        {others.map((other) => <Link key={other.id} className="text-indigo-600 hover:text-indigo-800" to={`/legal/${other.id}`}>{other.title}</Link>)}
        <Link className="text-indigo-600 hover:text-indigo-800" to="/login">Sign in</Link>
      </nav>
      <LegalMarkdown markdown={document.markdown} />
    </main>
  );
}

/** Signed-in view under the app layout, with a switcher between the three documents. */
export function PoliciesPage() {
  const { legal, document } = useDocument();
  if (legal.isLoading) return <LoadingSpinner label="Loading..." />;
  if (legal.error || !legal.data || !document) return <p role="alert" className="p-6 text-red-700">{apiError(legal.error)}</p>;
  const order = ['terms', 'acceptable-use', 'privacy'];
  const documents = [...legal.data.documents].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <nav aria-label="Policy documents" className="flex flex-wrap gap-2 border-b border-gray-200">
        {documents.map((doc) => (
          <Link key={doc.id} to={`/policies/${doc.id}`} aria-current={doc.id === document.id ? 'page' : undefined}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${doc.id === document.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            {doc.title}
          </Link>
        ))}
      </nav>
      <h1 className="text-2xl font-bold text-gray-900">{document.title}</h1>
      {legal.data.draft ? <DraftNotice version={legal.data.version} /> : <p className="text-sm text-gray-500">Version {legal.data.version}</p>}
      <div className="rounded-lg border border-gray-200 bg-white p-6"><LegalMarkdown markdown={document.markdown} /></div>
    </div>
  );
}

export function PoliciesIndex() {
  return <Navigate to="/policies/terms" replace />;
}
